import React, { useState } from 'react';
import { DIAL_CODES } from '../../data/dialCodes';
import { X, Mail, Lock, User, Phone, ArrowRight, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { db, type PhoneVerificationPending } from '../../services/db';
import { isDisposableEmail } from '../../services/disposableEmail';
import { trackConversion, CONVERSIONS } from '../../services/analytics';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialMode?: 'login' | 'signup';
    onSuccess: (role: 'user' | 'admin') => void;
    message?: string;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'login', onSuccess, message }) => {
    const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [phoneDialCode, setPhoneDialCode] = useState('+1');
    const [phoneLocal, setPhoneLocal] = useState('');

    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [phoneVerification, setPhoneVerification] = useState<PhoneVerificationPending | null>(null);
    const [phoneVerificationCode, setPhoneVerificationCode] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsLoading(true);

        try {
            if (mode === 'signup') {
                if (isDisposableEmail(email)) {
                    throw new Error('Temporary email addresses are not allowed. Please use a permanent email address.');
                }
                if (password.length < 6) {
                    throw new Error('Password must be at least 6 characters');
                }
                if (!phoneLocal) {
                    throw new Error('Phone number is required');
                }
                const phone = phoneDialCode + phoneLocal;
                const result = await db.register(name, email, password, phone);
                if (result && 'pendingRegistrationId' in result) {
                    setPhoneVerification(result);
                    setPhoneVerificationCode('');
                    setSuccess(result.message);
                    return;
                }
                trackConversion(CONVERSIONS.SIGN_UP);
                onClose();
                window.location.href = `/verify-email?email=${encodeURIComponent(email)}`;
            } else {
                const userData = await db.login(email, password, rememberMe);
                if (userData.status === 'suspended') {
                    throw new Error('Account suspended. Contact support.');
                }
                await db.syncAll();
                trackConversion(CONVERSIONS.LOGIN);
                onSuccess(userData.role);
                onClose();
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyPhone = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!phoneVerification) return;

        setError('');
        setSuccess('');
        setIsLoading(true);

        try {
            await db.verifyPhoneRegistration(phoneVerification.pendingRegistrationId, phoneVerificationCode);
            trackConversion(CONVERSIONS.SIGN_UP);
            onClose();
            window.location.href = `/verify-email?email=${encodeURIComponent(phoneVerification.email || email)}`;
        } catch (err: any) {
            setError(err.message || 'Phone verification failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendPhoneCode = async () => {
        if (!phoneVerification) return;

        setError('');
        setSuccess('');
        setIsLoading(true);

        try {
            const resent = await db.resendPhoneRegistration(phoneVerification.pendingRegistrationId);
            setPhoneVerification(resent);
            setSuccess(resent.message);
        } catch (err: any) {
            setError(err.message || 'Failed to resend verification code');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-300">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
                >
                    <X size={18} className="text-gray-500" />
                </button>

                <div className="p-8">
                    <div className="flex items-center gap-2 mb-6">
                        <span className="text-2xl font-black text-[#ff4d00]">TRAFFIC</span>
                        <span className="text-xs font-bold bg-black text-white px-2 py-1 rounded uppercase">Creator</span>
                    </div>

                    {message && (
                        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                            <CheckCircle className="text-green-500 shrink-0" size={20} />
                            <span className="text-green-700 text-sm font-bold">{message}</span>
                        </div>
                    )}

                    <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
                        <button
                            onClick={() => { setMode('login'); setError(''); setSuccess(''); setPhoneVerification(null); }}
                            className={`flex-1 py-3 rounded-lg text-sm font-bold uppercase tracking-wide transition-all ${mode === 'login' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            Login
                        </button>
                        <button
                            onClick={() => { setMode('signup'); setError(''); setSuccess(''); }}
                            className={`flex-1 py-3 rounded-lg text-sm font-bold uppercase tracking-wide transition-all ${mode === 'signup' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            Sign Up
                        </button>
                    </div>

                    {error && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                            <AlertTriangle className="text-red-500 shrink-0" size={18} />
                            <span className="text-red-700 text-sm font-bold">{error}</span>
                        </div>
                    )}

                    {success && (
                        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                            <CheckCircle className="text-green-500 shrink-0" size={18} />
                            <span className="text-green-700 text-sm font-bold">{success}</span>
                        </div>
                    )}

                    {mode === 'signup' && phoneVerification ? (
                        <form onSubmit={handleVerifyPhone} className="space-y-5">
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wide block mb-2">Verification Code</label>
                                <div className="relative">
                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        required
                                        value={phoneVerificationCode}
                                        onChange={(e) => setPhoneVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                        className="w-full bg-gray-50 border border-gray-200 p-4 pl-12 rounded-xl text-sm font-medium text-gray-900 focus:border-[#ff4d00] focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
                                        placeholder="1234"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    Code sent to {phoneVerification.phone} by {phoneVerification.verificationChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'}.
                                </p>
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading || phoneVerificationCode.length < 4}
                                className="w-full bg-[#ff4d00] text-white p-4 rounded-xl text-sm font-bold uppercase tracking-widest hover:bg-black transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="animate-spin" size={18} /> Verifying...
                                    </>
                                ) : (
                                    <>
                                        Verify Phone <ArrowRight size={18} />
                                    </>
                                )}
                            </button>
                            <div className="flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={handleResendPhoneCode}
                                    disabled={isLoading}
                                    className="text-xs font-bold text-[#ff4d00] uppercase tracking-wide hover:text-black disabled:opacity-50"
                                >
                                    Resend Code
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setPhoneVerification(null);
                                        setPhoneVerificationCode('');
                                        setSuccess('');
                                    }}
                                    disabled={isLoading}
                                    className="text-xs font-bold text-gray-400 uppercase tracking-wide hover:text-black disabled:opacity-50"
                                >
                                    Change Phone
                                </button>
                            </div>
                        </form>
                    ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {mode === 'signup' && (
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wide block mb-2">Full Name</label>
                                <div className="relative">
                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                    <input
                                        type="text"
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full bg-gray-50 border border-gray-200 p-4 pl-12 rounded-xl text-sm font-medium text-gray-900 focus:border-[#ff4d00] focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
                                        placeholder="John Doe"
                                    />
                                </div>
                            </div>
                        )}

                        {mode === 'signup' && (
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wide block mb-2">Phone Number</label>
                                <div className="flex gap-2">
                                    <select
                                        value={phoneDialCode}
                                        onChange={(e) => setPhoneDialCode(e.target.value)}
                                        className="bg-gray-50 border border-gray-200 p-4 rounded-xl text-sm font-medium text-gray-900 focus:border-[#ff4d00] focus:ring-2 focus:ring-orange-500/20 outline-none transition-all w-28 shrink-0"
                                    >
                                        {DIAL_CODES.map(({ code, label }) => (
                                            <option key={code} value={code}>{label}</option>
                                        ))}
                                    </select>
                                    <div className="relative flex-1">
                                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input
                                            type="tel"
                                            required
                                            value={phoneLocal}
                                            onChange={(e) => setPhoneLocal(e.target.value.replace(/[^\d\s\-()]/g, ''))}
                                            className="w-full bg-gray-50 border border-gray-200 p-4 pl-12 rounded-xl text-sm font-medium text-gray-900 focus:border-[#ff4d00] focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
                                            placeholder="170 1234567"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wide block mb-2">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value.trim())}
                                    className="w-full bg-gray-50 border border-gray-200 p-4 pl-12 rounded-xl text-sm font-medium text-gray-900 focus:border-[#ff4d00] focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
                                    placeholder="you@example.com"
                                />
                            </div>
                            {mode === 'signup' && email && isDisposableEmail(email) && (
                                <p className="text-red-500 text-xs mt-2 font-bold flex items-center gap-1">
                                    <AlertTriangle size={12} /> Temporary emails not allowed. Please use your real email.
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wide block mb-2">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-gray-50 border border-gray-200 p-4 pl-12 rounded-xl text-sm font-medium text-gray-900 focus:border-[#ff4d00] focus:ring-2 focus:ring-orange-500/20 outline-none transition-all"
                                    placeholder="••••••••"
                                />
                            </div>
                            {mode === 'signup' && password.length > 0 && password.length < 6 && (
                                <p className="text-red-500 text-xs mt-2 font-bold">Minimum 6 characters required</p>
                            )}
                        </div>

                        {mode === 'login' && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="rememberMeModal"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="w-4 h-4 text-[#ff4d00] border-gray-300 rounded focus:ring-[#ff4d00]"
                                />
                                <label htmlFor="rememberMeModal" className="ml-2 text-xs font-bold text-gray-400 uppercase tracking-wide cursor-pointer">
                                    Remember me for 30 days
                                </label>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading || (mode === 'signup' && isDisposableEmail(email))}
                            className="w-full bg-[#ff4d00] text-white p-4 rounded-xl text-sm font-bold uppercase tracking-widest hover:bg-black transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin" size={18} /> Processing...
                                </>
                            ) : (
                                <>
                                    {mode === 'login' ? 'Sign In' : 'Create Free Account'} <ArrowRight size={18} />
                                </>
                            )}
                        </button>
                    </form>
                    )}

                    {mode === 'signup' && (
                        <div className="mt-4 flex items-center justify-center gap-4 text-xs text-gray-400 font-medium">
                            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-green-500" /> No credit card</span>
                            <span className="flex items-center gap-1"><CheckCircle size={12} className="text-green-500" /> 2,000 free visitors</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
